import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "./Button";
import { useOtpSend, useOtpVerify } from "../hooks/useApi";
import { authApi } from "../services/api";

const DIGIT_MAP = "۰۱۲۳۴۵۶۷۸۹٠١٢٣٤٥٦٧٨٩";
const LATIN = "01234567890123456789";
function normalizeDigits(v: string): string {
  let out = "";
  for (const ch of v) {
    const idx = DIGIT_MAP.indexOf(ch);
    out += idx !== -1 ? LATIN[idx % 10] : ch;
  }
  return out;
}

function isValidPhoneDigits(digitsOnly: string): boolean {
  return /^09\d{9}$/.test(digitsOnly);
}

export default function OtpAuth({
  submitLabel = "تایید و ورود",
  onSuccessPhone,
  mode = "login",
  initialPhone,
}: {
  submitLabel?: string;
  onSuccessPhone?: string;
  mode?: "login" | "signup";
  initialPhone?: string;
}) {
  const [phone, setPhone] = useState(initialPhone || "");
  const [phoneDigits, setPhoneDigits] = useState(
    initialPhone ? normalizeDigits(initialPhone).replace(/[^0-9]/g, "") : ""
  );
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [cooldown, setCooldown] = useState(0);
  const [checking, setChecking] = useState(false);
  const [showExistsPrompt, setShowExistsPrompt] = useState(false);
  const timerRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const otpSend = useOtpSend();
  const otpVerify = useOtpVerify();

  useEffect(() => {
    if (initialPhone) {
      const digits = normalizeDigits(initialPhone).replace(/[^0-9]/g, "");
      setPhone(initialPhone);
      setPhoneDigits(digits);
    }
  }, [initialPhone]);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0 && timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [cooldown]);

  const startCooldown = (sec: number) => {
    setCooldown(sec);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
  };

  const handlePhoneChange = (v: string) => {
    setPhone(v);
    const digits = normalizeDigits(v).replace(/[^0-9]/g, "");
    setPhoneDigits(digits);
    if (showExistsPrompt) setShowExistsPrompt(false);
  };

  const handleCodeChange = (v: string) => {
    const digits = normalizeDigits(v).replace(/[^0-9]/g, "").slice(0, 5);
    setCode(digits);
  };

  const doSend = () => {
    const digits = normalizeDigits(phone).replace(/[^0-9]/g, "");
    if (!isValidPhoneDigits(digits)) return;
    otpSend.mutate(
      { phone: digits },
      {
        onSuccess: (data: Record<string, unknown>) => {
          setStep("code");
          setCode("");
          const expires = (data.expires_in as number) || 120;
          startCooldown(expires);
        },
      }
    );
  };

  const handleSend = async () => {
    const digits = normalizeDigits(phone).replace(/[^0-9]/g, "");
    if (!isValidPhoneDigits(digits)) return;

    // Signup mode: check if phone already registered before sending OTP
    if (mode === "signup") {
      setChecking(true);
      setShowExistsPrompt(false);
      try {
        const res = await authApi.otpCheck(digits);
        const exists = (res.data as Record<string, unknown>)?.exists as boolean;
        if (exists) {
          setShowExistsPrompt(true);
          setChecking(false);
          return;
        }
      } catch {
        // if check fails, fall through to send (let backend handle)
      }
      setChecking(false);
    }

    doSend();
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    const digits = normalizeDigits(phone).replace(/[^0-9]/g, "");
    const codeDigits = normalizeDigits(code).replace(/[^0-9]/g, "");
    if (codeDigits.length !== 5) return;
    otpVerify.mutate({ phone: digits, code: codeDigits });
  };

  const handleResend = () => {
    if (cooldown > 0) return;
    // For signup resend we already know it's not existing (checked before), so direct send
    if (mode === "signup" && showExistsPrompt) return;
    doSend();
  };

  const handleEditPhone = () => {
    setStep("phone");
    setCode("");
    setShowExistsPrompt(false);
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setCooldown(0);
  };

  const handleGoToLogin = () => {
    const digits = normalizeDigits(phone).replace(/[^0-9]/g, "");
    navigate(`/login?phone=${encodeURIComponent(digits)}`);
  };

  // Phone step
  if (step === "phone") {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        className="space-y-4"
      >
        <div>
          <label className="block text-small font-medium text-text-secondary mb-1.5">شماره موبایل</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            className={`w-full h-10 px-3.5 rounded-btn border text-body text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all ${
              phoneDigits && !isValidPhoneDigits(phoneDigits) ? "border-status-error-text" : "border-line"
            }`}
            placeholder="09xxxxxxxxx"
            dir="ltr"
            inputMode="numeric"
            maxLength={11}
            autoComplete="tel"
            required
          />
          {phoneDigits && !isValidPhoneDigits(phoneDigits) && (
            <p className="text-xs text-status-error-text mt-1.5">شماره موبایل باید ۱۱ رقم و با 09 شروع شود</p>
          )}
          <p className="text-xs text-text-tertiary mt-1.5">کد تایید ۵ رقمی به این شماره پیامک خواهد شد</p>
        </div>

        {showExistsPrompt && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 space-y-3">
            <p className="text-small text-amber-800 leading-relaxed">
              این شماره قبلاً ثبت شده است. می‌خواهید با این شماره وارد شوید؟
            </p>
            <div className="flex gap-2">
              <Button type="button" onClick={handleGoToLogin} className="flex-1 !py-2">
                رفتن به صفحه ورود
              </Button>
              <button
                type="button"
                onClick={() => setShowExistsPrompt(false)}
                className="flex-1 py-2 px-3 rounded-btn border border-line bg-surface text-small font-medium text-text-secondary hover:bg-surface-base transition-colors"
              >
                انصراف
              </button>
            </div>
          </div>
        )}

        <Button
          type="submit"
          className="w-full !py-2.5"
          loading={otpSend.isPending || checking}
          disabled={!isValidPhoneDigits(phoneDigits) || showExistsPrompt}
        >
          ارسال کد تایید
        </Button>

        {onSuccessPhone && (
          <p className="text-xs text-center text-text-tertiary">{onSuccessPhone}</p>
        )}
      </form>
    );
  }

  // Code step
  return (
    <form onSubmit={handleVerify} className="space-y-4">
      <div className="rounded-lg bg-primary-light/30 border border-primary/20 px-3.5 py-2.5 flex items-center justify-between gap-2">
        <span className="text-small text-text-secondary">
          کد به شماره <span dir="ltr" className="font-medium text-text-main">{phoneDigits}</span> ارسال شد
        </span>
        <button type="button" onClick={handleEditPhone} className="text-xs text-primary hover:underline font-medium shrink-0">
          ویرایش
        </button>
      </div>

      <div>
        <label className="block text-small font-medium text-text-secondary mb-1.5">کد تایید</label>
        <input
          type="text"
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          className="w-full h-11 px-3.5 rounded-btn border border-line text-center tracking-[0.45em] text-lg font-medium text-text-main placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
          placeholder="─ ─ ─ ─ ─"
          dir="ltr"
          inputMode="numeric"
          maxLength={5}
          autoComplete="one-time-code"
          autoFocus
        />
        <div className="flex items-center justify-between mt-2">
          {cooldown > 0 ? (
            <span className="text-xs text-text-tertiary">ارسال مجدد تا {cooldown} ثانیه دیگر</span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={otpSend.isPending}
              className="text-xs text-primary hover:underline font-medium disabled:opacity-50"
            >
              ارسال مجدد کد
            </button>
          )}
          <span className="text-xs text-text-tertiary">{code.length}/5</span>
        </div>
      </div>

      <Button type="submit" className="w-full !py-2.5" loading={otpVerify.isPending} disabled={code.length !== 5}>
        {submitLabel}
      </Button>

      <button
        type="button"
        onClick={handleEditPhone}
        className="w-full text-center text-small text-text-tertiary hover:text-text-secondary transition-colors"
      >
        بازگشت و ویرایش شماره
      </button>
    </form>
  );
}
